<script lang="ts">
  import { api } from '../lib/api';
  import { session } from '../lib/session.svelte';
  import {
    encryptPayload, decryptPayload, base64ToBytes, bytesToBase64,
    type PrivatePayload,
  } from '../lib/crypto';
  import { privateTagIndex } from '../lib/tagIndex';
  import TagInput from './TagInput.svelte';
  import type {
    Activity, Visibility, CreateActivityRequest, UpdateActivityRequest,
  } from '../../../shared/types';

  interface Props {
    /** Pass an existing activity to enter edit mode; omit/null to create. */
    existing?: Activity | null;
    /** Used for both "created" and "updated". The parent decides what to do. */
    onCreated: (a: Activity) => void;
    onCancel?: () => void;
  }
  let { existing = null, onCreated, onCancel }: Props = $props();

  const isEdit = $derived(existing !== null);

  // --- Initial state from `existing` ---------------------------------------
  // For private rows we decrypt locally so the form fields show real values.
  function decryptExisting(): PrivatePayload | null {
    if (!existing || existing.visibility !== 'private') return null;
    if (!session.dek) return null;
    try {
      return decryptPayload(
        { ciphertext: base64ToBytes(existing.ciphertext), nonce: base64ToBytes(existing.nonce) },
        session.dek,
      );
    } catch { return null; }
  }
  const initialPriv = decryptExisting();

  // svelte-ignore state_referenced_locally
  let visibility: Visibility = $state(existing?.visibility ?? 'private');
  // svelte-ignore state_referenced_locally
  let title = $state(
    existing
      ? (existing.visibility === 'private' ? initialPriv?.title ?? '' : existing.title)
      : '',
  );
  // svelte-ignore state_referenced_locally
  let description = $state(
    existing
      ? (existing.visibility === 'private'
          ? initialPriv?.description ?? ''
          : existing.description ?? '')
      : '',
  );
  // svelte-ignore state_referenced_locally
  let tags: string[] = $state(
    existing
      ? (existing.visibility === 'private' ? initialPriv?.tags ?? [] : [...existing.tags])
      : [],
  );
  // svelte-ignore state_referenced_locally
  let locLabel = $state(
    existing
      ? (existing.visibility === 'private'
          ? initialPriv?.loc_label ?? ''
          : existing.loc_label ?? '')
      : '',
  );
  // svelte-ignore state_referenced_locally
  let scheduled = $state(
    existing
      ? toLocalInput(
          existing.visibility === 'private'
            ? initialPriv?.scheduled_at ?? null
            : existing.scheduled_at,
        )
      : '',
  );

  // Original visibility + tags (for diffing the private-tag index on save).
  const originalVisibility: Visibility | null = existing?.visibility ?? null;
  const originalPrivateTags: string[] = initialPriv?.tags ?? [];

  let error: string | null = $state(null);
  let busy = $state(false);

  function toLocalInput(epochSeconds: number | null | undefined): string {
    if (!epochSeconds) return '';
    const d = new Date(epochSeconds * 1000);
    // <input type="datetime-local"> wants YYYY-MM-DDTHH:MM in local time.
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }

  function scheduledEpoch(): number | null {
    if (!scheduled) return null;
    const d = new Date(scheduled);
    if (Number.isNaN(d.getTime())) return null;
    return Math.floor(d.getTime() / 1000);
  }

  /**
   * Build the request body. The server expects either a private shape
   * (ciphertext+nonce) OR a plaintext shape (title/tags/loc/scheduled). When
   * switching visibility, we send the new shape and let the server clear
   * the columns of the old shape (see server/activities.ts:patchActivity).
   */
  function buildBody(): CreateActivityRequest {
    if (visibility === 'private') {
      if (!session.dek) {
        // Caller (submit) catches this and surfaces a useful message
        // pointing at the unlock banner. The error code is a sentinel,
        // not user-facing text.
        throw new Error('dek_missing');
      }
      const payload: PrivatePayload = {
        title: title.trim(),
        tags,
        description: description.trim() || undefined,
        loc_label: locLabel || undefined,
        scheduled_at: scheduledEpoch() ?? undefined,
      };
      const sealed = encryptPayload(payload, session.dek);
      return {
        visibility,
        ciphertext: bytesToBase64(sealed.ciphertext),
        nonce: bytesToBase64(sealed.nonce),
      };
    }
    return {
      visibility,
      title: title.trim(),
      description: description.trim() || null,
      tags,
      loc_label: locLabel || null,
      scheduled_at: scheduledEpoch(),
    };
  }

  /**
   * Keep the IndexedDB private tag index in sync with what the user actually
   * has stored as private. Three cases:
   *   - private → private:        diff: release old, record new
   *   - private → semi/public:    release all
   *   - semi/public → private:    record all
   *   - semi/public → semi/public: no-op (tags are server-side)
   */
  async function syncPrivateTagIndex(): Promise<void> {
    const wasPrivate = originalVisibility === 'private';
    const isPrivate = visibility === 'private';
    if (!wasPrivate && !isPrivate) return;
    if (wasPrivate && !isPrivate) {
      await privateTagIndex.release(originalPrivateTags);
      return;
    }
    if (!wasPrivate && isPrivate) {
      await privateTagIndex.record(tags);
      return;
    }
    // both private: release the ones that went away, record the new ones
    const newSet = new Set(tags);
    const oldSet = new Set(originalPrivateTags);
    await privateTagIndex.release(originalPrivateTags.filter((t) => !newSet.has(t)));
    await privateTagIndex.record(tags.filter((t) => !oldSet.has(t)));
  }

  async function submit(e: SubmitEvent) {
    e.preventDefault();
    error = null;
    if (!title.trim()) {
      error = 'Tittel er påkrevd.';
      return;
    }
    // Pre-flight: if the user picked Privat but the DEK is missing
    // (post-reload state), point them at the unlock banner instead of
    // letting them fill out a form they can't submit. Same check in
    // buildBody catches direct mis-calls.
    if (visibility === 'private' && !session.dek) {
      error = 'Privat innhold er låst etter sideoppdatering. Lås opp øverst på siden, så kan du lagre.';
      return;
    }
    busy = true;
    try {
      const body = buildBody();
      const result: Activity = isEdit && existing
        ? await api.updateActivity(existing.id, body as UpdateActivityRequest)
        : await api.createActivity(body);
      await syncPrivateTagIndex();
      onCreated(result);
    } catch (err) {
      if (err instanceof Error && err.message === 'dek_missing') {
        error = 'Privat innhold er låst etter sideoppdatering. Lås opp øverst på siden, så kan du lagre.';
      } else {
        error = err instanceof Error ? err.message : String(err);
      }
    } finally {
      busy = false;
    }
  }
</script>

<form onsubmit={submit} class="card" aria-labelledby="form-h">
  <h2 id="form-h">{isEdit ? 'Rediger aktivitet' : 'Ny vinteraktivitet'}</h2>

  <label for="vis">Synlighet</label>
  <select id="vis" bind:value={visibility}>
    <option value="private">Privat (ende-til-ende-kryptert)</option>
    <option value="friends">Venner (kun de jeg har lagt til)</option>
    <option value="semi">Halv-offentlig (uten navn)</option>
    <option value="public">Offentlig (med navn)</option>
  </select>
  {#if isEdit && originalVisibility !== null && originalVisibility !== visibility}
    <p class="muted" style="margin-top: 0.25rem;">
      Bytter synlighet fra <em>{originalVisibility}</em> til <em>{visibility}</em> ved lagring.
      {#if visibility === 'private'}
        Innholdet krypteres lokalt før det sendes.
      {:else if originalVisibility === 'private'}
        Innholdet sendes som klartekst etter dekryptering lokalt.
      {/if}
    </p>
  {/if}

  <label for="title">Tittel</label>
  <input id="title" type="text" bind:value={title} required />

  <label for="desc">Beskrivelse (valgfritt)</label>
  <textarea id="desc" rows="4" bind:value={description}
            placeholder="Detaljer, lenker, hva som helst"></textarea>
  <p class="muted" style="font-size: 0.8rem; margin: 0.2rem 0 0;">
    Du kan bruke Markdown — <code>**fet**</code>, <code>*kursiv*</code>,
    <code>[lenke](https://…)</code>, lister med <code>-</code>.
  </p>

  <div id="tags-label" style="margin: 0.75rem 0 0.25rem; font-weight: 500;">Etiketter</div>
  <div role="group" aria-labelledby="tags-label">
    <TagInput visibility={visibility} bind:tags onChange={(t) => (tags = t)} />
  </div>

  <label for="loc">Sted (valgfritt)</label>
  <input id="loc" type="text" bind:value={locLabel}
         placeholder="f.eks. Sognsvann, Oslo" />

  <label for="sched">Tidspunkt (valgfritt)</label>
  <input id="sched" type="datetime-local" bind:value={scheduled} />

  {#if error}<p class="error" role="alert">{error}</p>{/if}

  <div class="row" style="margin-top: 1rem;">
    <button class="primary" type="submit" disabled={busy}>
      {busy ? 'Lagrer …' : isEdit ? 'Lagre endringer' : 'Legg til'}
    </button>
    {#if onCancel}
      <button type="button" onclick={onCancel}>Avbryt</button>
    {/if}
  </div>
</form>
